<template>
	<!-- Mobile: receipt bar with the details toggle, the total and the next action. -->
	<div class="lg:hidden flex items-center justify-between gap-3">
		<div class="relative flex items-center gap-3 min-w-0">
			<button
				type="button"
				class="bg-primary rounded-xl w-10 h-10 flex justify-center items-center flex-none"
				:aria-label="t('view_reservation')"
				:aria-expanded="isReceiptOpen"
				data-cy="mobile-receipt-toggle"
				@click="receipt?.toggle()"
			>
				<Recipe class="text-white w-6" />
			</button>
			<div class="min-w-0">
				<button
					type="button"
					class="flex items-center gap-1.5 text-sm font-bold text-primary whitespace-nowrap"
					:aria-expanded="isReceiptOpen"
					data-cy="mobile-receipt-link"
					@click="receipt?.toggle()"
				>
					{{ isReceiptOpen ? t('hide_details') : t('view_details') }}
					<Chevron class="size-3 transition-transform" :class="{ 'rotate-180': isReceiptOpen }" />
				</button>
				<PriceText :value="$flowstore.checkout.meta.total_price" class="block font-bold text-xl" />
			</div>
			<Transition name="hint">
				<p
					v-if="hintOpen"
					class="receipt-hint absolute bottom-full left-0 mb-3 whitespace-nowrap rounded-md bg-white px-3 py-2 text-xs shadow-tooltip"
					data-cy="mobile-receipt-hint"
				>
					{{ t('view_reservation') }}
				</p>
			</Transition>
		</div>
		<ActionButton
			variant="secondary"
			class="flex-none font-bold"
			:class="{ invisible: !navStore.next }"
			:disabled="nextDisabled"
			:data-cy="nextButtonCy"
			@click="goToNext"
			>{{ nextButtonLabel }}</ActionButton
		>
	</div>

	<!-- Desktop: progress steps with previous / next actions. -->
	<nav class="hidden lg:flex justify-between items-center gap-8 h-12">
		<div>
			<Transition name="fade">
				<ActionButton
					v-show="navStore.hasPreviousStep"
					variant="light"
					class="font-bold min-w-32"
					:disabled="$loadingstore.validating_booking"
					@click="gotoPreviousStep()"
					>{{ t('previous') }}</ActionButton
				>
			</Transition>
		</div>
		<div class="gap-6 lg:gap-8 flex-1 max-w-screen-md flex">
			<div :class="{ 'text-primary': navStore.current === 'reservation' }" class="w-1/4">
				<ProgressIndicator :progress="navStore.progress.reservation" />
				<span class="block text-sm truncate">
					<span class="font-bold mr-2">1</span>
					{{ t('steps.reservation') }}
				</span>
			</div>

			<div
				v-if="navStore.progress.choices !== undefined"
				:class="{ 'text-primary': navStore.current === 'choices' }"
				class="w-1/4"
			>
				<ProgressIndicator :progress="navStore.progress.choices" />
				<span class="block text-sm truncate">
					<span class="font-bold mr-2">2</span>
					{{ t('steps.choices') }}
				</span>
			</div>

			<div :class="{ 'text-primary': navStore.current === 'customer' }" class="w-1/4">
				<ProgressIndicator :progress="navStore.progress.customer" />
				<span class="block text-sm truncate">
					<span class="font-bold mr-2">{{ navStore.progress.choices !== undefined ? 3 : 2 }}</span>
					{{ t('steps.customer_details') }}
				</span>
			</div>

			<div class="w-1/4">
				<ProgressIndicator :progress="navStore.progress.complete" />
				<span class="block text-sm truncate">
					<span class="font-bold mr-2">{{ navStore.progress.choices !== undefined ? 4 : 3 }}</span>
					{{ t('steps.complete') }}
				</span>
			</div>
		</div>
		<div>
			<ActionButton
				variant="secondary"
				class="font-bold min-w-32"
				:class="{ invisible: !navStore.next }"
				:disabled="nextDisabled"
				:data-cy="nextButtonCyDesktop"
				@click="goToNext"
				>{{ nextButtonLabel }}</ActionButton
			>
		</div>
	</nav>

	<ReceiptOverlay ref="receipt" @open="onReceiptOpen" @close="isReceiptOpen = false" />
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { isAxiosError } from 'axios';
import ActionButton from '@/components/ActionButton.vue';
import ProgressIndicator from '@/components/ProgressIndicator.vue';
import Recipe from '@/theme/icons/recipe.svg?component';
import Chevron from '@thermenresorts/icons/svg/chevron-down.svg?component';
import PriceText from '@/components/PriceText.vue';
import ReceiptOverlay from '@/components/ReceiptOverlay.vue';
import bridge from '@thermenresorts/bridge';
import { useFlowStore } from '@/stores/useFlowStore';
import { useGTMStore} from '@/stores/useGTMStore';
import { useLoadingStore } from '@/stores/useLoadingStore';
import { useNavigationStore } from '@/stores/useNavigationStore';
import { resolveBookingErrorTarget } from '@/stores/bookingErrorRedirect';
import { useSkipUpsellStep } from '@/composables/useSkipUpsellStep';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const flowStore = useFlowStore();
const gtmStore = useGTMStore();
const loadingStore = useLoadingStore();
const navStore = useNavigationStore();
const { skipEmptyChoicesAndNavigate } = useSkipUpsellStep();

const HINT_ENTER_DELAY = 100;
const HINT_EXIT_AFTER = 5000;

const receipt = ref<InstanceType<typeof ReceiptOverlay>>();
const isReceiptOpen = ref(false);
const hintOpen = ref(false);
const hintTimers: number[] = [];

// Mimics the chatbot intro on the websites: point out the receipt every time the flow opens, then collapse again.
onMounted(() => {
  hintTimers.push(window.setTimeout(() => (hintOpen.value = true), HINT_ENTER_DELAY));
  hintTimers.push(window.setTimeout(() => (hintOpen.value = false), HINT_ENTER_DELAY + HINT_EXIT_AFTER));
});

onBeforeUnmount(() => {
  hintTimers.forEach(timer => window.clearTimeout(timer));
});

const onReceiptOpen = () => {
  isReceiptOpen.value = true;
  hintOpen.value = false;
};

// The final "complete" step submits the booking (pay) instead of navigating forward.
const isPayStep = computed(() => navStore.next?.name === 'complete');
const nextButtonLabel = computed(() => t(isPayStep.value ? 'pay' : 'next'));
const nextButtonCy = computed(() => (isPayStep.value ? 'step-navigation-button-pay' : 'step-navigation-button-next'));
// Mobile and desktop render the same action; keep their test hooks distinct so a selector never matches both.
const nextButtonCyDesktop = computed(() => `${nextButtonCy.value}-desktop`);
const nextDisabled = computed(() => (isPayStep.value ? loadingStore.validating_booking : loadingStore.validating_step));

const goToNext = () => (isPayStep.value ? createBooking() : gotoNextStep());

/**
 * Navigate to the next step in the flow.
 */
const gotoNextStep = async () => {
  hintOpen.value = false;
  if (receipt.value) receipt.value.close();

  try {
    const validated = await flowStore.validateStep(navStore.currentRouteValidator);
    if (validated) return await skipEmptyChoicesAndNavigate(true);
  } catch (error) {
    console.warn('Validation checkout failed.', error);
  }
};

/**
 * Navigate to the previous step in the flow.
 */
const gotoPreviousStep = async () => {
  return await skipEmptyChoicesAndNavigate(false);
};

/**
 * Create a booking.
 */
const createBooking = async () => {
  if (receipt.value) receipt.value.close();

  try {
    const booking = await flowStore.createBooking();
    if (booking) {
      // Reset the remebered path, so we don't return
      // to the personal details screen after the booking is started.
      navStore.resetRememberedPath();

      // Send the GTM DataLayer event
      gtmStore.sendEvent('add_personal_info', {}, true, false);

      bridge.sendMessage('reservation-flow', 'redirect', { tag: 'START_PAYMENT', url: booking.redirect_url });

      if (!bridge.isIntergrated) window.location.href = booking.redirect_url;
    }
  } catch (error) {
    console.warn('Creating a booking is cancelled due server or validation errors.');

    if (isAxiosError(error) && error.response && error.response?.data?.errors) {
      const messages: Record<string, string[]> = error.response.data.errors;

      const target = resolveBookingErrorTarget(Object.keys(messages));
      if (target === 'first_step') await navStore.gotoFirstStep();
      if (target === 'room_choice_step') await navStore.gotoStep('arrangement_hotel_choices');
    }
  }
};
</script>
